import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import WebIcon from '@mui/icons-material/Web';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CloudIcon from '@mui/icons-material/Cloud';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import DataObjectIcon from '@mui/icons-material/DataObject';

const services = [
  {
    title: 'Web Development',
    description: 'Custom, responsive websites that showcase your unique business and connect with your local customers.',
    icon: WebIcon,
    color: '#5271FF'
  },
  {
    title: 'Mobile Apps',
    description: 'User-friendly mobile applications that help small businesses provide better service and engagement.',
    icon: SmartphoneIcon,
    color: '#FF914D'
  },
  {
    title: 'Cloud Solutions',
    description: 'Affordable cloud systems that streamline operations and save you time for what matters most.',
    icon: CloudIcon,
    color: '#54C5EB'
  },
  {
    title: 'Digital Marketing',
    description: 'Local-focused marketing strategies that bring qualified customers to your small business.',
    icon: TrendingUpIcon,
    color: '#E36588'
  },
  {
    title: 'UI/UX Design',
    description: 'Intuitive, attractive designs that reflect your brand personality and improve customer experience.',
    icon: DesignServicesIcon,
    color: '#A084E8'
  },
  {
    title: 'Custom Software',
    description: 'Tailored software solutions for specific business challenges, from appointment systems to inventory management.',
    icon: DataObjectIcon,
    color: '#6CD4A6'
  }
];

const ServiceCard = ({ service, index }) => {
  const theme = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <Card 
        elevation={0}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid rgba(230, 230, 230, 0.5)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <Box 
          sx={{ 
            p: 3, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'rgba(248, 249, 255, 0.7)'
          }}
        >
          <Box 
            sx={{
              width: 70,
              height: 70,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${service.color}15`
            }}
          >
            <service.icon 
              sx={{ 
                fontSize: 36, 
                color: service.color
              }} 
            />
          </Box>
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          <Typography 
            gutterBottom 
            variant="h6" 
            component="h3" 
            fontWeight={600}
            sx={{ mb: 1 }}
          >
            {service.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {service.description}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ServicesSection = () => {
  return (
    <Box 
      sx={{ 
        py: { xs: 8, md: 12 },
        backgroundColor: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(82,113,255,0.05) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-5%',
          left: '-5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,145,77,0.05) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div"
              color="primary"
              fontWeight={600}
              sx={{ mb: 1, letterSpacing: 2 }}
            >
              OUR EXPERTISE
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Services for Small Businesses
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                fontSize: '1.1rem'
              }}
            >
              Affordable digital solutions to help your small business connect with customers, streamline operations, and grow locally.
            </Typography>
          </Box>
        </motion.div>
        
        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <ServiceCard service={service} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ServicesSection; 